import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import ProsecutorApi, { FullProsecutorDto } from '../../api/ProsecutorApi';
import './ProsecutorSelect.scss';

interface ProsecutorSelectProps {
  value?: FullProsecutorDto | null;
  onChange: (prosecutor: FullProsecutorDto | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  courtFilter?: string; // Optional filter by court name (for backwards compatibility)
  courtGuidFilter?: string; // Optional filter by court guid (more precise)
}

const ProsecutorSelect: React.FC<ProsecutorSelectProps> = ({
  value,
  onChange,
  label = "Select Prosecutor",
  placeholder = "Search for prosecutors...",
  disabled = false,
  error = false,
  helperText,
  required = false,
  courtFilter,
  courtGuidFilter,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FullProsecutorDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchTerm: string) => {
      // If no search term and no court filter, clear options
      if (!searchTerm && !courtFilter && !courtGuidFilter) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const searchParams: any = {
          size: 50, // Limit results
          Prosecutor_Active: true, // Only show active prosecutors
        };

        if (searchTerm) {
          searchParams.search = searchTerm;
        }

        // Prefer Court_guid for more precise filtering, fall back to Court_Name
        if (courtGuidFilter) {
          searchParams.Court_guid = courtGuidFilter;
        } else if (courtFilter) {
          searchParams.Court_Name = courtFilter;
        }

        const response = await ProsecutorApi.searchProsecutors(searchParams);
        setOptions(response.prosecutors);
      } catch (error) {
        console.error('Error searching prosecutors:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [courtFilter, courtGuidFilter]
  );

  // Effect to trigger search when input changes
  useEffect(() => {
    if (open) {
      debouncedSearch(inputValue);
    }
  }, [inputValue, debouncedSearch, open]);

  // Load initial options when component opens
  useEffect(() => {
    if (open && options.length === 0 && !inputValue) {
      debouncedSearch('');
    }
  }, [open, debouncedSearch, options.length, inputValue]);

  // Load prosecutors when court filter changes
  useEffect(() => {
    if ((courtGuidFilter || courtFilter) && open) {
      debouncedSearch(inputValue);
    }
  }, [courtGuidFilter, courtFilter, open, debouncedSearch, inputValue]);

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: FullProsecutorDto | null) => {
    onChange(newValue);
  };

  const formatOptionLabel = (option: FullProsecutorDto) => {
    return `${option.Prosecutor_Name} - ${option.court?.Court_Name || 'Unknown Court'}`;
  };

  const renderOption = (props: any, option: FullProsecutorDto) => (
    <Box component="li" {...props} key={option.Prosecutor_guid}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Typography variant="body1" component="div">
          {option.Prosecutor_Name}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          {option.Prosecutor_Email}
        </Typography>
        {option.court && (
          <Chip
            label={option.court.Court_Name}
            size="small"
            variant="outlined"
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <div className="prosecutor-select">
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={formatOptionLabel}
        renderOption={renderOption}
        loading={loading}
        disabled={disabled}
        isOptionEqualToValue={(option, value) => 
          option.Prosecutor_guid === value?.Prosecutor_guid
        }
        filterOptions={(options) => options} // Disable built-in filtering since we're doing server-side search
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            required={required}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.Prosecutor_guid}
              label={option.Prosecutor_Name}
              size="small"
            />
          ))
        }
        noOptionsText={
          inputValue || courtFilter || courtGuidFilter ? 
            "No prosecutors found" : 
            "Start typing to search for prosecutors"
        }
        loadingText="Searching prosecutors..."
      />
    </div>
  );
};

export default ProsecutorSelect;
